import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Route, Switch, Router as WouterRouter, useLocation } from 'wouter';
import { Toaster } from 'sonner';
import { useEffect, useState, lazy, Suspense, Component, type ReactNode, type ErrorInfo } from 'react';
import { SupabaseAuthProvider } from '@/contexts/SupabaseAuthContext';
import { useGetMe, getGetMeQueryKey } from '@workspace/api-client-react';
import { refreshRegistryUrls } from '@/services/RegistryUrlValidationService';
import { PlatformMonitor }     from '@/services/platform/PlatformMonitor';
import ProcessingTray from '@/components/knowledge/ProcessingTray';
import { hasRequiredRole, type UserRole } from '@/lib/permissions';

// ── Stale Chunk Crash Guard ──────────────────────────────────────────────────
// After any new deployment, Vite's hashed chunk filenames change. Users with a
// cached index.html reference old chunk URLs which return 404. React.lazy()
// rejects with a "Failed to fetch dynamically imported module" error (Vite) or
// "ChunkLoadError" (webpack-style builds). Without a boundary, the Suspense
// fallback hangs and every lazy-loaded page shows a blank screen.
//
// Fix: ChunkErrorBoundary intercepts the failure, reloads once (guarded by
// sessionStorage to prevent infinite reload loops), and shows a friendly error
// UI if the page is still broken after the reload.
//
// Scope: every ProtectedRoute, RoleProtectedRoute, and public lazy route.
// ─────────────────────────────────────────────────────────────────────────────

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  // Vite: dynamic import failure
  if (error.message.includes('Failed to fetch dynamically imported module')) return true;
  // webpack-style ChunkLoadError
  if (error.name === 'ChunkLoadError') return true;
  // Safari / Firefox wording
  if (error.message.includes('Loading chunk') && error.message.includes('failed')) return true;
  if (error.message.includes('Importing a module script failed')) return true;
  return false;
}

const CHUNK_RELOAD_KEY = 'wr-chunk-reload-attempted';

class ChunkErrorBoundary extends Component<{ children: ReactNode }, { crashed: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { crashed: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      const alreadyTried = (() => {
        try { return sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1'; } catch { return false; }
      })();
      if (!alreadyTried) {
        try { sessionStorage.setItem(CHUNK_RELOAD_KEY, '1'); } catch { /* ignore */ }
        console.warn('[ChunkErrorBoundary] stale chunk detected — reloading once.', error.message);
        window.location.reload();
        return;
      }
    }
    this.setState({ crashed: true });
    console.error('[ChunkErrorBoundary] component error (post-reload or non-chunk):', error, info);
  }

  render() {
    if (this.state.crashed) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: '100vh', background: '#030d1f', color: 'rgba(255,255,255,0.70)',
          fontFamily: 'monospace', gap: 16, padding: 32, textAlign: 'center',
        }}>
          <div style={{ fontSize: 10, letterSpacing: '0.24em', color: 'rgba(96,165,250,0.65)' }}>
            WORKROAMER™
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
            Something went wrong loading this page.
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.42)', maxWidth: 340 }}>
            This can happen right after an update. Refreshing usually fixes it.
          </div>
          <button
            onClick={() => {
              try { sessionStorage.removeItem(CHUNK_RELOAD_KEY); } catch { /* ignore */ }
              window.location.reload();
            }}
            style={{
              marginTop: 8, padding: '10px 24px', background: '#2563eb',
              border: 'none', borderRadius: 8, color: '#fff', fontFamily: 'monospace',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', cursor: 'pointer',
            }}
          >
            REFRESH PAGE
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Public / auth pages (eager — needed immediately before auth resolves) ─────
import Login              from '@/pages/Login';
import SignupPage         from '@/pages/SignupPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import VerifyEmailPage    from '@/pages/VerifyEmailPage';
import AuthCallbackPage   from '@/pages/AuthCallbackPage';
import AuthResetPage      from '@/pages/AuthResetPage';
import NotFound           from '@/pages/not-found';
import { Shell }          from '@/components/layout/Shell';
// ── Trust & Transparency: legal pages (public, lazy — readable before signup) ─
const TermsPage        = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage      = lazy(() => import('@/pages/PrivacyPage'));
const SecurityTrustPage = lazy(() => import('@/pages/SecurityTrustPage'));
const AIPolicyPage     = lazy(() => import('@/pages/AIPolicyPage'));

// ── Always-mounted app components (eager) ─────────────────────────────────────
import LearnToRoamCard from '@/components/LearnToRoamCard';
import RoamerLocalCard  from '@/components/RoamerLocalCard';

// ── Standard #2: ALL route pages are lazy-loaded ──────────────────────────────
// Each page is a separate Vite code-split chunk, fetched only on first visit.
// ProtectedRoute wraps every Component in <Suspense> so the main thread is
// never blocked and the initial JS bundle stays minimal.
const Home                       = lazy(() => import('@/pages/Home'));
const Dashboard                  = lazy(() => import('@/pages/Dashboard'));
const TimerPage                  = lazy(() => import('@/pages/TimerPage'));
const IncomePage                 = lazy(() => import('@/pages/IncomePage'));
const HealthPage                 = lazy(() => import('@/pages/HealthPage'));
const ConnectPage                = lazy(() => import('@/pages/ConnectPage'));
const MarketplacePage            = lazy(() => import('@/pages/MarketplacePage'));
const WalletPage                 = lazy(() => import('@/pages/WalletPage'));
const PassportPage               = lazy(() => import('@/pages/PassportPage'));
const VisaPage                   = lazy(() => import('@/pages/VisaPage'));
const StayPage                   = lazy(() => import('@/pages/StayPage'));
const CompaniesPage              = lazy(() => import('@/pages/CompaniesPage'));
const GreenDayPage               = lazy(() => import('@/pages/GreenDayPage'));
const OpportunitiesPage          = lazy(() => import('@/pages/OpportunitiesPage'));
const OperatorControlCenter      = lazy(() => import('@/pages/OperatorControlCenter'));
const OperatorInsightsPage       = lazy(() => import('@/pages/OperatorInsightsPage'));
const MissionSourceHealthPage    = lazy(() => import('@/pages/MissionSourceHealthPage'));
const PaymentCenterPage          = lazy(() => import('@/pages/PaymentCenterPage'));
const EarningsReportsPage        = lazy(() => import('@/pages/EarningsReportsPage'));
const ExportDataPage             = lazy(() => import('@/pages/ExportDataPage'));
const TaxCenterPage              = lazy(() => import('@/pages/TaxCenterPage'));
const PaymentMethodsPage         = lazy(() => import('@/pages/PaymentMethodsPage'));
const ProfilePage                = lazy(() => import('@/pages/ProfilePage'));
const AboutPage                  = lazy(() => import('@/pages/AboutPage'));
const LearnDashboardPage         = lazy(() => import('@/pages/LearnDashboardPage'));
const OpportunityPreferencesPage = lazy(() => import('@/pages/OpportunityPreferencesPage'));
// Role-gated: chunk not downloaded until RoleGuard confirms access (defence-in-depth)
const MissionControlPage         = lazy(() => import('@/pages/MissionControlPage'));
const NetworkPage                = lazy(() => import('@/pages/NetworkPage'));
const BetaLabPage                = lazy(() => import('@/pages/BetaLabPage'));
const DeveloperSettingsPage      = lazy(() => import('@/pages/DeveloperSettingsPage'));
const DevCenterPage              = lazy(() => import('@/pages/DevCenterPage'));
const AuditReport                = lazy(() => import('@/pages/AuditReport'));
const HarvestDiagnosticsPage     = lazy(() => import('@/pages/HarvestDiagnosticsPage'));
const EmailPreviewPage           = lazy(() => import('@/pages/dev/EmailPreviewPage'));
const SessionProbePage           = lazy(() => import('@/pages/dev/SessionProbePage'));
const BoardingPassPreviewPage    = lazy(() => import('@/pages/dev/BoardingPassPreviewPage'));
const RoamerControlPreviewPage   = lazy(() => import('@/pages/dev/RoamerControlPreviewPage'));
import { ThemeProvider } from '@/components/theme-provider';
import { useTheme } from '@/lib/use-theme';

const queryClient = new QueryClient();

function Redirect({ to }: { to: string }) {
  const [, setLocation] = useLocation();
  useEffect(() => { setLocation(to); }, [to, setLocation]);
  // Return a themed placeholder instead of null so there is never a blank
  // white flash during the brief window before the useEffect navigation fires.
  return <div className="h-screen w-full bg-background" />;
}

/**
 * OPERATOR IDENTITY™ (2.06.0): Auth gating is handled inside <Shell>.
 * Shell calls useGetMe (always enabled), publishes SessionRestored/SessionExpired
 * events, and redirects to /login when the session is missing or expired.
 * All ProtectedRoute components are wrapped in Shell — the protection lives there.
 */
/** L-02: DEV card-preview page — isolated component harness for visual regression. */
const DevCardPreviewPage = () => (
  <div style={{ background: '#05070f', minHeight: '100vh', padding: 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
    {/* LOCAL INTELLIGENCE™ (2.04.1): live weather + Roamer Score™ verification */}
    <div style={{ width: 840 }}>
      <RoamerLocalCard />
    </div>
    <div style={{ width: 400 }}>
      <LearnToRoamCard onNavigate={() => {}} />
    </div>
  </div>
);

function ProtectedRoute({ component: Component, path }: { component: React.ComponentType, path: string }) {
  // Standard #2: Suspense boundary — all route components are lazy-loaded chunks.
  // The fallback matches the app background so there is no flash of white on navigation.
  // ChunkErrorBoundary: reloads once on ChunkLoadError (stale cache after deploy).
  return (
    <Route path={path}>
      <Shell>
        <ChunkErrorBoundary>
          <Suspense fallback={<div className="h-screen w-full bg-background" />}>
            <Component />
          </Suspense>
        </ChunkErrorBoundary>
      </Shell>
    </Route>
  );
}

/**
 * Role-gated route — redirects to /home when the user lacks the required role.
 * Shell still handles auth (→ /login). This only enforces the RBAC layer.
 */
function RoleGuard({ minRole, children }: { minRole: UserRole; children: React.ReactNode }) {
  const [, setLocation] = useLocation();
  const { data, isLoading } = useGetMe({ query: { queryKey: getGetMeQueryKey(), retry: false, enabled: true } });
  const role = (data as { role?: string } | undefined)?.role ?? 'USER';
  const hasAccess = hasRequiredRole(role, minRole);

  useEffect(() => {
    if (!isLoading && !hasAccess) setLocation('/home');
  }, [isLoading, hasAccess, setLocation]);

  if (isLoading || !hasAccess) return <div className="h-screen w-full bg-background" />;
  return <>{children}</>;
}

/**
 * Route that enforces a minimum RBAC role.
 *
 * Security model (defence-in-depth):
 *   1. Shell — redirects to /login when no session exists (standard auth gate).
 *   2. RoleGuard — checks authenticated user's role from the server; redirects
 *      to /home when the role is insufficient. The component is never rendered.
 *   3. Lazy import — because Component is React.lazy(), its JS chunk is only
 *      fetched by the browser AFTER React attempts to render it. Since
 *      RoleGuard short-circuits before rendering <Component /> for unauthorized
 *      users, the chunk is NEVER downloaded for users who lack the role.
 *   4. Server — requireRole() middleware on every /api/ops/* and sensitive route
 *      returns 403 even if a client bypasses the UI checks.
 *
 * The Suspense fallback shows while the lazy chunk is downloading (owner only).
 */
function RoleProtectedRoute({
  component: Component, path, minRole,
}: { component: React.ComponentType; path: string; minRole: UserRole }) {
  return (
    <Route path={path}>
      <Shell>
        <RoleGuard minRole={minRole}>
          <ChunkErrorBoundary>
            <Suspense fallback={<div className="h-screen w-full bg-background" />}>
              <Component />
            </Suspense>
          </ChunkErrorBoundary>
        </RoleGuard>
      </Shell>
    </Route>
  );
}

/**
 * Mission Control guard — two-factor access: OWNER role + server-verified PIN.
 *
 * After Shell confirms the user is authenticated, this guard:
 *   1. Reads the user's role from useGetMe; redirects to /home if not OWNER.
 *   2. Calls GET /api/auth/mc-status — backed by the server-side PIN unlock
 *      registry (set by POST /api/auth/verify-owner-pin); redirects to /about
 *      if PIN has not been completed this server session.
 *   3. Only renders children (MissionControlPage) when both checks pass.
 *      Because MissionControlPage is a React.lazy() import, its JS chunk is
 *      never fetched until this guard allows rendering.
 */
function MCGuard({ children }: { children: React.ReactNode }) {
  const [, setLocation] = useLocation();
  const { data, isLoading } = useGetMe({
    query: { queryKey: getGetMeQueryKey(), retry: false, enabled: true },
  });
  const role = (data as { role?: string } | undefined)?.role ?? 'USER';
  const hasOwnerRole = hasRequiredRole(role, 'OWNER');
  const [pinStatus, setPinStatus] = useState<'checking' | 'unlocked' | 'locked'>('checking');

  useEffect(() => {
    if (isLoading) return;

    if (!hasOwnerRole) {
      setLocation('/home');
      return;
    }

    // Check server-backed PIN unlock registry.
    const base = (import.meta.env.BASE_URL ?? '/').replace(/\/$/, '');
    fetch(`${base}/api/auth/mc-status`, { credentials: 'include' })
      .then(r => r.json() as Promise<{ unlocked: boolean }>)
      .then(d => {
        if (d.unlocked) {
          setPinStatus('unlocked');
        } else {
          setPinStatus('locked');
          setLocation('/about');
        }
      })
      .catch(() => {
        setPinStatus('locked');
        setLocation('/about');
      });
    // hasOwnerRole and setLocation are stable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hasOwnerRole]);

  if (isLoading || pinStatus === 'checking') {
    return <div className="h-screen w-full bg-background" />;
  }
  if (pinStatus === 'locked' || !hasOwnerRole) {
    return <div className="h-screen w-full bg-background" />;
  }
  return <>{children}</>;
}

function Router() {
  return (
    <Switch>
      {/* ── Public routes (no auth required) ── */}
      <Route path="/login"           component={Login} />
      <Route path="/signup"          component={SignupPage} />
      <Route path="/forgot-password" component={ForgotPasswordPage} />
      <Route path="/verify-email"    component={VerifyEmailPage} />
      {/* Supabase Auth callback routes — must be public (no auth required) */}
      <Route path="/auth/callback"   component={AuthCallbackPage} />
      <Route path="/auth/reset"      component={AuthResetPage} />
      {/* Trust & Transparency: legal pages — public so users can read before signing up */}
      <Route path="/terms"><ChunkErrorBoundary><Suspense fallback={<div className="h-screen w-full bg-background" />}><TermsPage /></Suspense></ChunkErrorBoundary></Route>
      <Route path="/privacy"><ChunkErrorBoundary><Suspense fallback={<div className="h-screen w-full bg-background" />}><PrivacyPage /></Suspense></ChunkErrorBoundary></Route>
      <Route path="/security-trust"><ChunkErrorBoundary><Suspense fallback={<div className="h-screen w-full bg-background" />}><SecurityTrustPage /></Suspense></ChunkErrorBoundary></Route>
      <Route path="/ai-policy"><ChunkErrorBoundary><Suspense fallback={<div className="h-screen w-full bg-background" />}><AIPolicyPage /></Suspense></ChunkErrorBoundary></Route>
      <Route path="/">{() => <Redirect to="/home" />}</Route>

      {/* ── Protected routes (enforced by Shell → useGetMe) ── */}
      <ProtectedRoute path="/home"             component={Home} />
      <ProtectedRoute path="/dashboard"        component={Dashboard} />
      <ProtectedRoute path="/timer"            component={TimerPage} />
      <ProtectedRoute path="/income"           component={IncomePage} />
      <ProtectedRoute path="/health"           component={HealthPage} />
      <ProtectedRoute path="/connect"          component={ConnectPage} />
      <ProtectedRoute path="/marketplace"      component={MarketplacePage} />
      <ProtectedRoute path="/wallet"           component={WalletPage} />
      <ProtectedRoute path="/passport"         component={PassportPage} />
      <ProtectedRoute path="/visa"             component={VisaPage} />
      <ProtectedRoute path="/stay"             component={StayPage} />
      <ProtectedRoute path="/companies"        component={CompaniesPage} />
      <ProtectedRoute path="/green-day"        component={GreenDayPage} />
      <ProtectedRoute path="/opportunities"    component={OpportunitiesPage} />
      <ProtectedRoute path="/settings"         component={OperatorControlCenter} />
      <ProtectedRoute path="/insights"         component={OperatorInsightsPage} />
      {/* RBAC: dev/ops routes — OWNER role required (redirect to /home otherwise) */}
      <RoleProtectedRoute path="/audit"                    component={AuditReport}             minRole="OWNER" />
      <RoleProtectedRoute path="/dev/session-probe"         component={SessionProbePage}         minRole="OWNER" />
      <RoleProtectedRoute path="/dev/email-preview"        component={EmailPreviewPage}         minRole="OWNER" />
      <RoleProtectedRoute path="/dev/harvest-diagnostics"  component={HarvestDiagnosticsPage}  minRole="OWNER" />
      <RoleProtectedRoute path="/dev/center"               component={DevCenterPage}           minRole="OWNER" />
      {import.meta.env.DEV && <ProtectedRoute path="/dev/card-preview" component={DevCardPreviewPage} />}
      {import.meta.env.DEV && <Route path="/dev/boarding-pass-preview"><Suspense fallback={null}><BoardingPassPreviewPage /></Suspense></Route>}
      {import.meta.env.DEV && <Route path="/dev/roamer-control-preview"><Suspense fallback={null}><RoamerControlPreviewPage /></Suspense></Route>}
      {import.meta.env.DEV && <Route path="/dev/crash-test">{() => { function Bomb(): never { throw new Error('Test: AppErrorBoundary verification'); } return <Bomb />; }}</Route>}
      {/* Sprint 107.1: Mission Source Health moved to permanent operator route */}
      <ProtectedRoute path="/system/health"    component={MissionSourceHealthPage} />
      {/* Sprint 095: Payment Center — master payment dashboard */}
      <ProtectedRoute path="/payment-center"   component={PaymentCenterPage}   />
      {/* Sprint 100: Payment Suite — each Quick Action gets its own unique route */}
      <ProtectedRoute path="/earnings-reports" component={EarningsReportsPage}  />
      <ProtectedRoute path="/export-data"      component={ExportDataPage}       />
      <ProtectedRoute path="/tax-center"       component={TaxCenterPage}        />
      <ProtectedRoute path="/payment-methods"  component={PaymentMethodsPage}   />
      {/* Sprint 100: /dev/settings consolidated into Mission Control → DEVELOPER tab */}
      <Route path="/dev/settings">{() => <Redirect to="/mission-control" />}</Route>
      {/* Build 2.33.0: Operator Profile Management */}
      <ProtectedRoute path="/profile"          component={ProfilePage} />
      {/* Learn Dashboard — single canonical Learn page; /backpack and /vault redirect here */}
      <ProtectedRoute path="/learn-to-roam"    component={LearnDashboardPage} />
      {/* Sprint 101: Roamer Network™ (Module #8 — full UI) */}
      <ProtectedRoute path="/network"          component={NetworkPage} />
      {/* Sprint XXX: Opportunity Preferences™ & Career Explorer™ ecosystem */}
      <ProtectedRoute path="/opportunity-preferences" component={OpportunityPreferencesPage} />
      {/* RBAC: Beta Lab — BETA_TESTER role required */}
      <RoleProtectedRoute path="/beta-lab"         component={BetaLabPage}         minRole="BETA_TESTER" />
      {/* RBAC: Mission Control — OWNER role + server-verified PIN (two-factor access) */}
      <Route path="/mission-control">
        <Shell>
          <MCGuard>
            <Suspense fallback={<div className="h-screen w-full bg-background" />}>
              <MissionControlPage />
            </Suspense>
          </MCGuard>
        </Shell>
      </Route>
      {/* About WorkRoamer — hidden activation surface for Mission Control PIN */}
      <ProtectedRoute path="/about"            component={AboutPage} />
      {/* Sprint 116: canonical route aliases — CPO UX findings R-01/R-02 */}
      <Route path="/occ">{() => <Redirect to="/settings" />}</Route>
      <Route path="/backpack">{() => <Redirect to="/learn-to-roam" />}</Route>
      <Route path="/vault">{() => <Redirect to="/learn-to-roam" />}</Route>
      {/* BUG FREEZE™ Stage 2: /journey resolved a 404 — the Journey record
          lives in Roamer Passport™ until the dedicated Journey module ships. */}
      <Route path="/journey">{() => <Redirect to="/passport" />}</Route>
      {/* Supabase regression fix: missing canonical routes caused 404s for
          deep links and Sidebar entries. /academy → opportunity preferences
          (Career Explorer & Academy), /career → same, /missions → opportunities. */}
      <Route path="/academy">{() => <Redirect to="/opportunity-preferences" />}</Route>
      <Route path="/career">{() => <Redirect to="/opportunity-preferences" />}</Route>
      <Route path="/missions">{() => <Redirect to="/opportunities" />}</Route>
      <Route component={NotFound} />
    </Switch>
  );
}

// Sprint 019: Toaster adapts to the active theme's light/dark mode.
function ThemedToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme.isDark ? 'dark' : 'light'}
      position="bottom-right"
      richColors
      closeButton
    />
  );
}

export default function App() {
  useEffect(() => {
    // Standard #2: install platform performance monitor (fetch interceptor for latency tracking)
    PlatformMonitor.install();
    // Sprint 112: validate mission source URLs once on app mount (fire-and-forget)
    refreshRegistryUrls().catch(() => { /* non-critical */ });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SupabaseAuthProvider>
          <WouterRouter>
            <Router />
            <ThemedToaster />
            <ProcessingTray />
          </WouterRouter>
        </SupabaseAuthProvider>
      </ThemeProvider>
      {/* TanStack Query Devtools — DEV only, hidden in production (Task #38) */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />}
    </QueryClientProvider>
  );
}
